"use strict";
// Copyright © Veeam Software Group GmbH
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DataGridNewInteractor = void 0;
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const core_1 = require("@veeam/core");
const DataGridNew_1 = require("./..");
class DataGridNewInteractor extends core_1.Interactor {
    constructor(model, config) {
        super();
        this.model = model;
        this.config = config;
        this.subscriptions = new rxjs_1.Subscription();
        this.firstSortDirection = core_1.SORT_DIRECTION.asc;
        this.onItemsRendered = ({ overscanStartIndex, overscanStopIndex }) => {
            if (this.hasGroups()) {
                return;
            }
            const renderCount = overscanStopIndex - overscanStartIndex;
            const centralItemIndex = overscanStopIndex - Math.round(renderCount / 2);
            this.model.update((prevValue) => {
                var _a;
                if (prevValue.pagination) {
                    if (prevValue.pagination.limit < renderCount) {
                        prevValue.pagination.limit = renderCount;
                    }
                    const prevOffset = prevValue.prevOffset || 0;
                    const paginationLimit = prevValue.pagination.limit;
                    const maxIndex = paginationLimit + prevOffset;
                    const minIndex = prevOffset - 1;
                    if (maxIndex < overscanStopIndex || minIndex > overscanStartIndex) {
                        prevValue.pagination.offset = Math.min(Math.max(0, centralItemIndex - Math.round(paginationLimit / 2)), ((_a = prevValue.totalCount) !== null && _a !== void 0 ? _a : Infinity) - paginationLimit);
                        if (prevValue.pagination.offset < 0) {
                            prevValue.pagination.offset = 0;
                        }
                    }
                }
            });
        };
    }
    getPersistentStorage() {
        var _a, _b, _c, _d;
        if (this.persistentStorage) {
            return this.persistentStorage;
        }
        if ((_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.persistent) === null || _b === void 0 ? void 0 : _b.storage) {
            this.persistentStorage = this.config.persistent.storage;
            return this.persistentStorage;
        }
        if ((_d = (_c = this.config) === null || _c === void 0 ? void 0 : _c.persistent) === null || _d === void 0 ? void 0 : _d.key) {
            this.persistentStorage = new DataGridNew_1.DataGridNewLocalPersistentStorage(this.config.persistent.key);
        }
        else {
            this.persistentStorage = new DataGridNew_1.DataGridNewInMemoryPersistentStorage();
        }
        return this.persistentStorage;
    }
    supressMetaLoading(isSupress) {
        var _a, _b;
        (_b = (_a = this.config).supressMetaLoading) === null || _b === void 0 ? void 0 : _b.call(_a, isSupress);
    }
    updatePersistConfig() {
        var _a, _b, _c;
        const storage = this.getPersistentStorage();
        const columnSizes = (_a = storage.read().columnSizes) !== null && _a !== void 0 ? _a : {};
        const columnVisibility = (_b = storage.read().columnVisibility) !== null && _b !== void 0 ? _b : {};
        const columnOrders = (_c = storage.read().columnOrders) !== null && _c !== void 0 ? _c : {};
        this.persistentConfig = {
            columnSizes,
            columnVisibility,
            columnOrders
        };
    }
    didActivate() {
        return __awaiter(this, void 0, void 0, function* () {
            this.subscriptions.add(this.model.fieldChanges('pagination').subscribe(() => {
                this.supressMetaLoading(!this.config.isPagePagination);
                this.model.load().finally(() => {
                    this.supressMetaLoading(false);
                });
            }));
            this.subscriptions.add((0, rxjs_1.merge)(this.model.fieldChanges('sorting'), this.model.fieldChanges('expanded'), this.model.fieldChanges('grouping')).subscribe(() => {
                this.model.load();
            }));
            this.subscriptions.add(this.model.fieldChanges('filters').subscribe(() => {
                var _a;
                if (((_a = this.model.getValue().pagination) === null || _a === void 0 ? void 0 : _a.offset) && this.config.isPagePagination) {
                    this.model.update((prev) => {
                        if (prev.pagination) {
                            prev.pagination.offset = 0;
                        }
                    });
                    return;
                }
                this.model.load();
            }));
            this.subscriptions.add(this.model
                .getMetaAsObservable()
                .pipe((0, operators_1.map)((item) => item.outdated))
                .pipe((0, operators_1.distinctUntilChanged)())
                .subscribe((outdated) => {
                var _a, _b;
                if (outdated) {
                    (_a = this.presenter) === null || _a === void 0 ? void 0 : _a.showOutdatedNotification();
                    return;
                }
                (_b = this.presenter) === null || _b === void 0 ? void 0 : _b.dismissNotification();
            }));
            if (this.config.isPagePagination !== undefined) {
                this.model.setShouldActualizeSelection(this.config.isPagePagination);
            }
            if (this.config.autoLoad) {
                yield this.model.load();
            }
        });
    }
    willDeactivate() {
        return __awaiter(this, void 0, void 0, function* () {
            this.subscriptions.unsubscribe();
        });
    }
    toggleSort(key, collation, firstSortDirection) {
        this.model.update((prevValue) => {
            var _a, _b, _c;
            const sorting = (_b = (_a = prevValue.sorting) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null;
            const prevDirection = sorting && sorting.key === key ? sorting.direction : null;
            const prevCollation = sorting && sorting.key === key ? sorting.collation : null;
            const newCollation = (_c = collation !== null && collation !== void 0 ? collation : prevCollation) !== null && _c !== void 0 ? _c : undefined;
            const direction = firstSortDirection !== null && firstSortDirection !== void 0 ? firstSortDirection : this.firstSortDirection;
            if (prevDirection === null) {
                prevValue.sorting = [
                    {
                        key,
                        direction: direction,
                        collation: newCollation
                    }
                ];
                return;
            }
            if (this.config.hasNoneSort && prevDirection !== direction) {
                prevValue.sorting = [];
                return;
            }
            const nextDirection = prevDirection === core_1.SORT_DIRECTION.asc ? core_1.SORT_DIRECTION.desc : core_1.SORT_DIRECTION.asc;
            prevValue.sorting = [
                {
                    key,
                    direction: nextDirection,
                    collation: newCollation
                }
            ];
        });
    }
    isSelectAll() {
        var _a;
        if ((_a = this.config) === null || _a === void 0 ? void 0 : _a.isRowDisabled) {
            return this.model.isAllItemsSelected((item, index) => {
                var _a, _b;
                return !((_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.isRowDisabled) === null || _b === void 0 ? void 0 : _b.call(_a, {
                    rowData: item,
                    rowIndex: index,
                    id: this.model.getItemID(item)
                }));
            });
        }
        return this.model.isAllItemsSelected();
    }
    isSelectSome() {
        var _a;
        if ((_a = this.config) === null || _a === void 0 ? void 0 : _a.isRowDisabled) {
            return this.model.isSomeItemsSelected((item, index) => {
                var _a, _b;
                return !((_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.isRowDisabled) === null || _b === void 0 ? void 0 : _b.call(_a, {
                    rowData: item,
                    rowIndex: index,
                    id: this.model.getItemID(item)
                }));
            });
        }
        return this.model.isSomeItemsSelected();
    }
    hasPagination() {
        const value = this.model.getValue();
        return (!!value.pagination && !!value.totalCount && value.totalCount > value.pagination.limit);
    }
    hasGrouping() {
        return this.model.hasGrouping();
    }
    hasGroups() {
        return this.model.hasGroups();
    }
    didPageChange(nextPage) {
        this.model.update((prevValue) => {
            if (prevValue.pagination) {
                prevValue.pagination.offset = (nextPage - 1) * prevValue.pagination.limit;
            }
        });
    }
    didSelectAllChange() {
        const nextIsAllSelected = !this.isSelectAll();
        this.model.update((prevValue) => {
            var _a, _b, _c, _d, _e, _f;
            if (!nextIsAllSelected) {
                if (((_b = (_a = this.config).isSelectionAllowed) === null || _b === void 0 ? void 0 : _b.call(_a, {
                    prevSelection: prevValue.selection.slice(),
                    nextSelection: []
                })) !== false) {
                    prevValue.selection = [];
                }
                return;
            }
            const nextValue = [];
            const items = this.model.getValue().items;
            for (let idx = 0; idx < items.length; idx++) {
                const item = items[idx];
                const id = this.model.getItemID(item);
                if ((_d = (_c = this.config) === null || _c === void 0 ? void 0 : _c.isRowDisabled) === null || _d === void 0 ? void 0 : _d.call(_c, { rowData: item, rowIndex: idx, id })) {
                    continue;
                }
                nextValue.push(id);
            }
            if (((_f = (_e = this.config).isSelectionAllowed) === null || _f === void 0 ? void 0 : _f.call(_e, {
                prevSelection: prevValue.selection.slice(0),
                nextSelection: nextValue.slice(0)
            })) !== false) {
                prevValue.selection = nextValue;
            }
        });
    }
    didSelectionChange(nextSelection) {
        this.model.update((prevValue) => {
            var _a, _b;
            if (((_b = (_a = this.config).isSelectionAllowed) === null || _b === void 0 ? void 0 : _b.call(_a, {
                prevSelection: prevValue.selection.slice(0),
                nextSelection: nextSelection.slice(0)
            })) !== false) {
                prevValue.selection = nextSelection;
            }
        });
    }
    getGroupedItemByIndex(index) {
        return this.model.getGroupedItemByIndex(index);
    }
    isGroupExpanded(groupKey) {
        return this.model.isGroupExpanded(groupKey);
    }
    toggleExpandedGroups(groupKey) {
        return __awaiter(this, void 0, void 0, function* () {
            this.supressMetaLoading(true);
            yield this.model.toggleExpandedGroups([groupKey]);
            this.supressMetaLoading(false);
        });
    }
    getPersistConfig() {
        if (!this.persistentConfig) {
            this.updatePersistConfig();
        }
        return this.persistentConfig;
    }
    didColumnSizeChange(payload) {
        const storage = this.getPersistentStorage();
        const prevData = storage.read();
        storage.save(Object.assign({}, prevData, {
            columnSizes: Object.assign({}, prevData.columnSizes, {
                [payload.columnId]: payload.width
            })
        }));
        this.updatePersistConfig();
    }
    didColumnOrderChange(payload) {
        const storage = this.getPersistentStorage();
        const prevData = storage.read();
        storage.save(Object.assign({}, prevData, {
            columnOrders: payload
        }));
        this.updatePersistConfig();
    }
    didColumnVisibilityChange(payload) {
        const storage = this.getPersistentStorage();
        const prevData = storage.read();
        storage.save(Object.assign({}, prevData, {
            columnVisibility: Object.assign({}, prevData.columnVisibility, {
                [payload.columnId]: payload.isShown
            })
        }));
        this.updatePersistConfig();
    }
    didSelectApplyFreshData() {
        this.model.applyFreshData();
    }
    didSelectIgnoreFreshData() {
        this.model.dropFreshData();
    }
    updateConfig(config) {
        if (config.isRowDisabled !== this.config.isRowDisabled) {
            this.config.isRowDisabled = config.isRowDisabled;
        }
        if (config.isSelectionAllowed !== this.config.isSelectionAllowed) {
            this.config.isSelectionAllowed = config.isSelectionAllowed;
        }
        if (config.isPagePagination !== this.config.isPagePagination) {
            this.config.isPagePagination = config.isPagePagination;
        }
    }
}
exports.DataGridNewInteractor = DataGridNewInteractor;
//# sourceMappingURL=DataGridNewInteractor.js.map